import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { ExerciseTagDto } from '../../../../shared/dtos/exerciseTag.dto';
import { ExerciseCategoryDto } from '../../../../shared/dtos/exerciseCategory.dto';
import { EntityScope } from '../../../../shared/utils/enums';
import PrimaryButton from '../../components/PrimaryButton';
import { ManagementMode, ScopeKey } from '../../util/types';
import ExerciseTagTable from './ExerciseTagTable';
import ExerciseTagModal from './ExerciseTagModal';

type ExerciseTagManagementProps = {
  mode: ManagementMode;
};

function isScopeKey(value: string | null): value is ScopeKey {
  return value === 'tenant' || value === 'core';
}

function getTagsEndpoint(mode: ManagementMode): string {
  if (mode === 'superAdmin') {
    return '/api/v1/exercise/tag/core';
  }

  return '/api/v1/exercise/tag';
}

function getCategoriesEndpoint(mode: ManagementMode): string {
  if (mode === 'superAdmin') {
    return '/api/v1/exercise/category/core';
  }

  return '/api/v1/exercise/category';
}

function getModalEntityScope(mode: ManagementMode): EntityScope {
  if (mode === 'superAdmin') {
    return EntityScope.CORE;
  }

  return EntityScope.TENANT;
}

const ExerciseTagManagement = ({ mode }: ExerciseTagManagementProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [tags, setTags] = useState<ExerciseTagDto[]>([]);
  const [categories, setCategories] = useState<ExerciseCategoryDto[]>([]);
  const [activeKey, setActiveKey] = useState<ScopeKey>('tenant');

  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editTag, setEditTag] = useState<ExerciseTagDto | null>(null);

  const isLoading: boolean = isLoadingTags || isLoadingCategories;
  const tagsEndpoint: string = getTagsEndpoint(mode);
  const categoriesEndpoint: string = getCategoriesEndpoint(mode);
  const modalEntityScope: EntityScope = getModalEntityScope(mode);
  const isAdminMode: boolean = mode === 'admin';

  const { tenantTags, coreTags } = useMemo(() => {
    const tenant: ExerciseTagDto[] = tags.filter((tag: ExerciseTagDto) => tag.scope === EntityScope.TENANT);
    const core: ExerciseTagDto[] = tags.filter((tag: ExerciseTagDto) => tag.scope === EntityScope.CORE);

    return { tenantTags: tenant, coreTags: core };
  }, [tags]);

  const fetchCategories: () => Promise<void> = useCallback(async () => {
    setIsLoadingCategories(true);

    try {
      const result: ExerciseCategoryDto[] = await authFetch(categoriesEndpoint, { method: 'GET' });
      setCategories(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCategories(false);
    }
  }, [authFetch, categoriesEndpoint]);

  const fetchTags: () => Promise<void> = useCallback(async () => {
    setIsLoadingTags(true);

    try {
      const result: ExerciseTagDto[] = await authFetch(tagsEndpoint, { method: 'GET' });
      setTags(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingTags(false);
    }
  }, [authFetch, tagsEndpoint]);

  useEffect(() => {
    void fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    void fetchTags();
  }, [fetchTags]);

  const closeModal = (): void => {
    setShowModal(false);
    setEditTag(null);
  };

  const openCreateModal = (): void => {
    setEditTag(null);
    setShowModal(true);
  };

  const openEditModal = (tag: ExerciseTagDto): void => {
    setEditTag(tag);
    setShowModal(true);
  };

  const renderCreateButton = (): ReactElement => {
    return (
      <PrimaryButton
        className="ms-auto"
        onClick={openCreateModal}
        disabled={isLoading}>
        {i18next.t('generic.create')}
      </PrimaryButton>
    )
  }

  const renderTable = (tableTags: ExerciseTagDto[], canEdit: boolean, showCreateButton: boolean): ReactElement => {
    return (
      <ExerciseTagTable
        tags={tableTags}
        categories={categories}
        isLoading={isLoading}
        toolbar={showCreateButton ? renderCreateButton() : undefined}
        onClick={canEdit ? openEditModal : undefined}
      />
    );
  };

  const renderAdminContent = (): ReactElement => {
    return (
      <Tabs
        activeKey={activeKey}
        onSelect={(key) => {
          if (isScopeKey(key)) {
            setActiveKey(key);
          }
        }}
        className="mb-3 pt-3"
        justify
        mountOnEnter
        unmountOnExit>
        <Tab eventKey="tenant" title={`${i18next.t('adminExerciseTags.tenantTags')} (${tenantTags.length})`}>
          {renderTable(tenantTags, true, true)}
        </Tab>

        <Tab eventKey="core" title={`${i18next.t('adminExerciseTags.coreTags')} (${coreTags.length})`}>
          {renderTable(coreTags, false, false)}
        </Tab>
      </Tabs>
    )
  }

  const renderSuperAdminContent = (): ReactElement => {
    return (
      <div className="pt-3">
        {renderTable(tags, true, true)}
      </div>
    )
  }

  return (
    <>
      <ExerciseTagModal
        show={showModal}
        entityScope={modalEntityScope}
        exerciseTag={editTag ?? undefined}
        categories={categories}
        onClose={closeModal}
        onSave={() => {
          void fetchTags();
        }}
      />

      {isAdminMode ? renderAdminContent() : renderSuperAdminContent()}
    </>
  );
};

export default ExerciseTagManagement;
